import IApplicationConfig from '^types/common/IApplicationConfig';
import UrlResolver from '^utils/urlResolver';
import ApplicationFeature from '^enums/ApplicationFeature';
import Brand from '^enums/Brand';
import BusinessType from '^enums/BusinessType';

const { isWidgetMode } = window.xprops;

class ApplicationConfig implements IApplicationConfig {
  private _urlResolver = new UrlResolver();

  private readonly _brand: IApplicationConfig['brand'];
  private readonly _modelCode: IApplicationConfig['modelCode'];
  private readonly _businessType: IApplicationConfig['businessType'];
  private readonly _configuratorUrl: IApplicationConfig['configuratorUrl'];
  private readonly _variantCodes: IApplicationConfig['variantCodes'];
  private readonly _features: IApplicationConfig['features'];
  private readonly _applicationFeatureAvailabilityMap: Record<ApplicationFeature, boolean>;

  private _resolveValue = <ResponseType = string>(name: keyof IApplicationConfig): ResponseType => {
    const value = isWidgetMode
      ? window.xprops[name]
      : this._urlResolver.getValue<ResponseType>(name);

    return (value ?? '') as ResponseType;
  };

  private _resolveArrayValue = <ResponseType = string[]>(name: keyof IApplicationConfig): ResponseType => {
    const value = isWidgetMode
      ? window.xprops[name]
      : this._urlResolver.getArrayValue<ResponseType>(name);

    return (value ?? []) as ResponseType;
  };

  private _resolveApplicationFeatureAvailabilityMap(): Record<ApplicationFeature, boolean> {
    return Object.values(ApplicationFeature).reduce(
      (map: Record<ApplicationFeature, boolean>, feature: ApplicationFeature): Record<ApplicationFeature, boolean> => {
        map[feature] = !!this._features?.includes(feature);

        return map;
      },
      {} as Record<ApplicationFeature, boolean>,
    );
  }

  public get brand(): IApplicationConfig['brand'] {
    return this._brand;
  }
  public get modelCode(): IApplicationConfig['modelCode'] {
    return this._modelCode;
  }
  public get businessType(): IApplicationConfig['businessType'] {
    return this._businessType;
  }
  public get configuratorUrl(): IApplicationConfig['configuratorUrl'] {
    return this._configuratorUrl;
  }
  public get variantCodes(): IApplicationConfig['variantCodes'] {
    return this._variantCodes;
  }
  public get features(): IApplicationConfig['features'] {
    return this._features;
  }
  public isFeatureAvailable (feature: ApplicationFeature): boolean {
    return this._applicationFeatureAvailabilityMap[feature];
  }

  public constructor() {
    this._brand = this._resolveValue<Brand>('brand');
    this._modelCode = this._resolveValue('modelCode');
    this._businessType = this._resolveValue('businessType') || BusinessType.Retail;
    this._configuratorUrl = this._resolveValue('configuratorUrl');
    this._variantCodes = this._resolveArrayValue('variantCodes');
    this._features = this._resolveArrayValue<ApplicationFeature[]>('features');
    this._applicationFeatureAvailabilityMap = this._resolveApplicationFeatureAvailabilityMap();
  }
}

export default new ApplicationConfig();

